<template>
  <div class="login_popup_input">
    <label class="custom-label">
      {{ label }}
    </label>
    <a-input
      class="ant-input-custom time-input"
      :class="required === true ? 'ant-required' : ''"
      type="text"
      :value="displayValue"
      :required="required"
      maxlength="5"
      placeholder="HH:MM"
      @input="handleInput"
      @blur="handleBlur"
    />

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const props = defineProps<{
  label?: string
  required?: boolean
  modelValue?: string
  requiredMessage?: string
  invalidMessage?: string
  invalidHhMessage?: string
  invalidMmMessage?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const errorMessage = ref('')

const displayValue = computed(() => props.modelValue ?? '')

const normalizeValue = (value: string): string => {
  return Array.from(value.trim())
    .map((char) => {
      const code = char.codePointAt(0)

      if (code !== undefined && code >= 0xff10 && code <= 0xff19) {
        return String.fromCharCode(code - 0xff10 + 0x30)
      }

      if (char === '：' || char === ':') {
        return ':'
      }

      if (code !== undefined && code >= 0x30 && code <= 0x39) {
        return char
      }

      return ''
    })
    .join('')
    .trim()
}

const formatTime = (value: string): string => {
  const normalized = normalizeValue(value)

  const match = normalized.match(/^(\d{1,2}):?(\d{1,2})$/)

  if (!match) {
    console.log('normalized not match')
    return normalized
  }

  const hour = Number(match[1])
  const minute = Number(match[2])

  if (hour > 23 || minute > 59) {
    return `${String(hour)}:${String(minute)}`
  }

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

const validateTime = (value: string): string => {
  let normalized = normalizeValue(value)

  if (!normalized) {
    return props.requiredMessage ?? ''
  }

  normalized = formatTime(normalized)
  if (!/^\d{2}:?\d{2}$/.test(normalized)) {
    return props.invalidMessage ?? ''
  }

  console.log('validateTime normalized', normalized)

  const [hourString, minuteString] = normalized.split(':')
  const hour = Number(hourString)
  const minute = Number(minuteString)

  if (hour > 23) {
    return props.invalidHhMessage ?? ''
  }

  if (minute > 59) {
    return props.invalidMmMessage ?? ''
  }

  return ''
}

const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement
  const value = normalizeValue(target.value)

  emit('update:modelValue', value)
  errorMessage.value = ''
}

const handleBlur = (event: FocusEvent): void => {
  const target = event.target as HTMLInputElement
  const formatted = formatTime(target.value)
  console.log('formatted', formatted)
  target.value = formatted

  emit('update:modelValue', formatted)
  errorMessage.value = validateTime(formatted)
}
</script>

<style scoped lang="scss">
.error-message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 0px;
  text-align: left;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.require {
  min-width: 64px;
  margin-right: 0.5em;
  font-size: 12px;
  height: 32px;
  color: $white;
  background-color: #cc0000;
  text-align: center;
  line-height: 32px;
}

.no-require {
  display: none;
  margin-right: 0.5em;
  min-width: 64px;
  font-size: 12px;
  height: 32px;
  padding: 4px;
}

:deep(.time-input.ant-input:focus) {
  border-color: #1890ff !important; /* Change this to your preferred ring color */
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.2) !important; /* The 'ring' effect */
  outline: none;
}
</style>
